import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { BellRing, Cpu, GitPullRequestArrow, ShieldAlert, ShieldCheck, TerminalSquare, TriangleAlert } from 'lucide-react';
import { api, type Job, type Verdict, type Alert, type LongJobView } from '../../api.js';
import { Section, Badge, Btn, EmptyState, Toggle, clock, modelName } from '../../ui/kit.js';
import { runAction } from '../../actions.js';
import { VERDICT_META, ACTION_WORDS, lifeMeta } from '../../phases.js';

/* The Finisher's own panels on the job page (V8 spec §4, §5, §8, §10, §11).
 *
 * Four things a person needs and could not see before: what the daemon
 * decided about each red and why, how much of each breaker is spent, what
 * ran on which model, and whether the notifications actually arrived. All
 * four are arguments a reader must be able to disagree with, so none of
 * them hides behind a colour — the reason is always beside the verdict.
 */

const pct = (used: number, limit: number) => (limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0);

// ── what the Finisher decided about every red ──────────────────────────────
export function FinisherHistory(props: { job: Job }) {
  const [verdicts, setVerdicts] = useState<Verdict[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const jobId = props.job.id;

  useEffect(() => {
    let live = true;
    void api
      .get<{ verdicts: Verdict[] }>(`/api/jobs/${jobId}/verdicts`)
      .then((r) => live && setVerdicts(r.verdicts ?? []))
      .catch(() => live && setVerdicts([]));
    return () => {
      live = false;
    };
  }, [jobId, props.job.lifecycle, props.job.headSha]);

  if (!verdicts) return null;

  const cycles = [...new Set(verdicts.map((v) => v.cycle))].sort((a, b) => b - a);

  return (
    <Section
      title="What the Finisher did about the red"
      aside={verdicts.length ? `${cycles.length} cycle${cycles.length === 1 ? '' : 's'}` : undefined}
    >
      {verdicts.length === 0 ? (
        <EmptyState icon={<GitPullRequestArrow />} title="No red has been read yet">
          When CI goes red the daemon reads each failing job's log, decides whether it is a real failure, a known flake or
          infrastructure, and records the decision here with the evidence behind it.
        </EmptyState>
      ) : (
        <div className="ops-fin__cycles">
          {cycles.map((cycle) => (
            <div className="ops-fin__cycle" key={cycle}>
              <p className="ops-fin__cyclehead">
                Cycle {cycle}
                <span>{clock(verdicts.find((v) => v.cycle === cycle)!.at)}</span>
              </p>
              {verdicts
                .filter((v) => v.cycle === cycle)
                .map((v) => {
                  const meta = VERDICT_META[v.verdict] ?? { label: v.verdict, tone: 'wait' as const };
                  const isOpen = open === v.id;
                  return (
                    <div className={`ops-fin__v ops-fin__v--${v.verdict}`} key={v.id}>
                      <div className="ops-fin__vhead">
                        <Badge tone={meta.tone}>{meta.label}</Badge>
                        <code className="ops-fin__vjob">{v.jobName}</code>
                      </div>
                      <p className="ops-fin__vreason">{v.reason}</p>
                      <p className="ops-fin__vaction">
                        The daemon {ACTION_WORDS[v.action] ?? v.action.replace(/_/g, ' ')}.
                      </p>
                      {v.specs.length ? (
                        <p className="ops-fin__vspecs">
                          {v.specs.map((s) => (
                            <code key={s}>{s}</code>
                          ))}
                        </p>
                      ) : null}
                      <div className="ops-fin__vfoot">
                        <code title="the failure's fingerprint">{v.fingerprint}</code>
                        {v.logTail ? (
                          <button type="button" className="ops-fin__vmore" onClick={() => setOpen(isOpen ? null : v.id)}>
                            {isOpen ? 'Hide the log' : 'Read the log'}
                          </button>
                        ) : null}
                        {v.verdict === 'real' ? (
                          <button
                            type="button"
                            className="ops-fin__vmore"
                            onClick={() =>
                              void api
                                .post(`/api/jobs/${jobId}/flake`, { fingerprint: v.fingerprint })
                                .then(() => setVerdicts((cur) => (cur ? [...cur] : cur)))
                                .catch(() => undefined)
                            }
                            title="Teach the Finisher that this fingerprint is a flake. It will re-run it once instead of fixing it."
                          >
                            Call this a flake
                          </button>
                        ) : null}
                      </div>
                      {isOpen && v.logTail ? <pre className="ops-fin__log">{v.logTail}</pre> : null}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── the circuit breakers, visible and editable (spec §5) ───────────────────
export function Breakers(props: { job: Job; onChanged: () => void }) {
  const { job } = props;
  const b = job.breakers;
  const [busy, setBusy] = useState(false);
  if (!b) return null;

  const raise = async (patch: Record<string, number>) => {
    setBusy(true);
    try {
      await api.post(`/api/jobs/${job.id}/breakers`, patch);
      props.onChanged();
    } finally {
      setBusy(false);
    }
  };

  const rows: { key: string; label: string; used: string; limit: string; p: number; raise?: () => void; note?: string }[] = [
    {
      key: 'fix',
      label: 'Fix cycles',
      used: String(b.fixCycles.used),
      limit: String(b.fixCycles.limit),
      p: pct(b.fixCycles.used, b.fixCycles.limit),
      raise: () => void raise({ fixCycles: b.fixCycles.limit + 4 }),
      note: 'Each red that is a real failure costs one.',
    },
    {
      key: 'spend',
      label: 'Spend',
      used: `$${(b.spendUsd.used ?? 0).toFixed(2)}`,
      limit: `$${b.spendUsd.limit.toFixed(0)}`,
      p: pct(b.spendUsd.used ?? 0, b.spendUsd.limit),
      raise: () => void raise({ spendUsd: b.spendUsd.limit + 50 }),
    },
    {
      key: 'wall',
      label: 'Wall clock',
      used: `${b.wallHours.used.toFixed(1)} h`,
      limit: `${b.wallHours.limit} h`,
      p: pct(b.wallHours.used, b.wallHours.limit),
      note: 'This one only notifies. It never stops a job.',
    },
    {
      key: 'restarts',
      label: 'Session restarts',
      used: String(b.restarts.used),
      limit: String(b.restarts.limit),
      p: pct(b.restarts.used, b.restarts.limit),
      raise: () => void raise({ restarts: b.restarts.limit + 5 }),
      note: 'A restart loop is a bug, not a strategy.',
    },
  ];

  return (
    <Section title="Circuit breakers" aside="every one bounded, nothing loops silently">
      <div className="ops-brk">
        {rows.map((r) => (
          <div className={`ops-brk__row${r.p >= 100 ? ' ops-brk__row--tripped' : ''}`} key={r.key}>
            <div className="ops-brk__head">
              <span className="ops-brk__label">{r.label}</span>
              <span className="ops-brk__count">
                {r.used} <span>of {r.limit}</span>
              </span>
            </div>
            <span className="ops-brk__bar">
              <span style={{ width: `${r.p}%` }} />
            </span>
            <div className="ops-brk__foot">
              {r.note ? <span className="ops-brk__note">{r.note}</span> : <span />}
              {r.raise && r.p >= 75 ? (
                <button type="button" className="ops-brk__raise" disabled={busy} onClick={r.raise}>
                  Raise it
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ── change the model or the account on a running job (spec §8) ─────────────
export function ModelControl(props: {
  job: Job;
  models: { id: string; label: string; cli?: string }[];
  accounts: { id: string; label: string }[];
  onChanged: () => void;
}) {
  const { job } = props;
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const change = useCallback(
    async (patch: Record<string, unknown>, what: string) => {
      setBusy(what);
      setSaid(null);
      try {
        const r = await api.post<{ takesEffect?: string }>(`/api/jobs/${job.id}/model`, patch);
        setSaid(`${what}. It takes effect ${r.takesEffect ?? 'on the next turn'}.`);
        props.onChanged();
      } catch (err) {
        setSaid((err as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [job.id, props],
  );

  const setAutoSwitch = useCallback(
    async (on: boolean) => {
      setBusy('auto-switch');
      setSaid(null);
      const r = await runAction(job.id, 'set_auto_switch_model', { enabled: on }, 'the model switch');
      setBusy(null);
      if (r?.ok) props.onChanged();
      else setSaid(r?.error ?? 'Not saved.');
    },
    [job.id, props],
  );

  if (!props.models.length) return null;
  const other = props.models.find((m) => m.id !== job.model);
  const pinWords =
    job.pinnedModel && job.pinnedAccount
      ? 'The model and the account were chosen by hand. A limit moves neither: the job parks until they have room again.'
      : job.pinnedModel
        ? 'The model was chosen by hand. A limit never moves it to the other model; the account still moves if that is switched on.'
        : 'The account was chosen by hand. A limit does not move it: the job parks until it has room again.';

  return (
    <Section title="Model and account" aside={job.pinnedModel || job.pinnedAccount ? 'chosen by hand' : 'chosen automatically'}>
      <ModelTruth job={job} />
      <p className="ops-mdl__explain">
        Changing either takes effect on the next turn. The worktree, the branch and the job's place in the pipeline are
        untouched. A session ending is a normal event, and the work carries on its branch.
      </p>
      <div className="ops-mdl__group">
        <span className="ops-mdl__glabel">
          <Cpu size={14} /> Model
        </span>
        <div className="ops-mdl__opts">
          {props.models.map((m) => (
            <button
              type="button"
              key={m.id}
              className={`ops-mdl__opt${job.model === m.id ? ' ops-mdl__opt--on' : ''}`}
              disabled={busy !== null || job.model === m.id}
              onClick={() => void change({ model: m.id }, `Model set to ${m.label}`)}
              data-testid={`set-model-${m.id}`}
            >
              <span>{m.label}</span>
              {m.cli ? <span className="ops-mdl__optid">{m.cli}</span> : null}
            </button>
          ))}
        </div>
      </div>
      {other ? (
        <div className="ops-mdl__switch ops-row--wrap">
          <div className="ops-row__main">
            <div className="ops-row__title">Move to the other model when this one runs out</div>
            <div className="ops-row__sub" data-testid="auto-switch-model-sub">
              {job.autoSwitchModel
                ? `On. If ${job.modelLabel} is out on every account, the job continues on ${other.label} and the page says so.`
                : `Off. If ${job.modelLabel} is out on every account, the job parks until it resets and never runs on ${other.label}.`}
            </div>
          </div>
          <Toggle
            checked={job.autoSwitchModel}
            onChange={(v) => void setAutoSwitch(v)}
            disabled={busy !== null || job.state === 'closed'}
            label="move to the other model when this one runs out"
            testId="auto-switch-model"
          />
        </div>
      ) : null}
      {props.accounts.length > 1 ? (
        <div className="ops-mdl__group">
          <span className="ops-mdl__glabel">Account</span>
          <div className="ops-mdl__opts">
            {props.accounts.map((a) => (
              <button
                type="button"
                key={a.id}
                className={`ops-mdl__opt${job.accountId === a.id ? ' ops-mdl__opt--on' : ''}`}
                disabled={busy !== null || job.accountId === a.id}
                onClick={() => void change({ accountId: a.id }, `Account set to ${a.label}`)}
                data-testid={`set-account-${a.id}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {job.pinnedModel || job.pinnedAccount ? (
        <div className="ops-mdl__pinned">
          <ShieldAlert size={14} />
          <span>{pinWords}</span>
          <Btn
            kind="ghost"
            disabled={busy !== null}
            onClick={() => void change({ model: job.model, accountId: job.accountId ?? undefined, pin: false }, 'Automatic selection back on')}
          >
            Let it choose
          </Btn>
        </div>
      ) : null}
      {said ? <p className="ops-mdl__said">{said}</p> : null}
      {job.attempts.length ? (
        <div className="ops-mdl__turns" data-testid="model-attempts">
          <p className="ops-mdl__turnhead">What each attempt ran on</p>
          {job.attempts.slice(-8).map((a) => (
            <p className={`ops-mdl__turn${a.modelMatch === false ? ' ops-mdl__turn--bad' : ''}`} key={a.n} data-testid={`model-attempt-${a.n}`}>
              <span className="ops-mdl__turnn">Attempt {a.n}</span>
              <code>{a.reportedModel ?? a.answeredModel ?? a.cliModel ?? a.model ?? job.model}</code>
              <span className="ops-mdl__turnsrc">
                {a.modelMatch === false ? (
                  <>
                    <TriangleAlert size={12} /> launched as {a.cliModel}
                  </>
                ) : a.modelMatch === true ? (
                  <>
                    <ShieldCheck size={12} /> reported by the session
                  </>
                ) : a.cliModel ? (
                  'launched, not reported yet'
                ) : (
                  'not recorded'
                )}
              </span>
              <span>{a.accountLabel}</span>
              {a.ending ? <span className="ops-mdl__turnend">{a.ending.kind}</span> : null}
            </p>
          ))}
        </div>
      ) : null}
    </Section>
  );
}

/* What the job asked for against what its session said it ran on, in the
   session's own words. The picker's label is a request; this is the answer,
   and a disagreement is the loudest thing in the section. */
function ModelTruth(props: { job: Job }) {
  const c = props.job.modelCheck;
  if (!c) return null;
  const running = props.job.state === 'running';
  const said = c.reported ?? c.answered;
  let tone: 'ok' | 'bad' | 'wait' = 'wait';
  let title: ReactNode;
  let body: ReactNode;
  if (c.state === 'mismatch') {
    tone = 'bad';
    const reportedOther = c.reported && c.reported !== c.launched ? c.reported : null;
    const answeredOther = c.answered && c.answered !== c.launched && c.answered !== reportedOther ? c.answered : null;
    title = 'Not the model that was asked for';
    body = (
      <>
        Asked for {c.picked.label} and launched as <code>{c.launched}</code> on attempt {c.attempt}.
        {reportedOther ? (
          <>
            {' '}
            The session reported <code>{reportedOther}</code>.
          </>
        ) : null}
        {answeredOther ? (
          <>
            {' '}
            The API answered as <code>{answeredOther}</code>.
          </>
        ) : null}
        {c.mismatchedAttempts.length > 1 ? ` Attempts ${c.mismatchedAttempts.join(', ')} ran on another model.` : ''}
      </>
    );
  } else if (c.state === 'confirmed') {
    tone = 'ok';
    title = (
      <>
        {running ? 'Running on' : 'Ran on'} {modelName(said)}
      </>
    );
    body = (
      <>
        <code>{said}</code>, as the session reported it in its own output
        {c.answered && c.reported ? ' and the API confirmed in its answer' : ''}. Attempt {c.attempt}.
      </>
    );
  } else if (c.state === 'waiting') {
    title = <>Launched with {modelName(c.launched)}</>;
    body = (
      <>
        <code>{c.launched}</code>. Waiting for the session to report the model it runs on.
      </>
    );
  } else {
    title = <>Starts on {c.picked.label}</>;
    body = (
      <>
        <code>{c.picked.cli}</code>. What it actually runs on is read from the session's own output once it starts.
      </>
    );
  }
  return (
    <div className={`ops-mdl__truth ops-mdl__truth--${tone}`} data-testid="model-truth" data-state={c.state}>
      <span className="ops-mdl__truthicon" aria-hidden="true">
        {tone === 'bad' ? <TriangleAlert size={18} /> : tone === 'ok' ? <ShieldCheck size={18} /> : <Cpu size={18} />}
      </span>
      <div className="ops-mdl__truthtext">
        <p className="ops-mdl__truthtitle">{title}</p>
        <p className="ops-mdl__truthbody">{body}</p>
        {c.nextTurn ? (
          <p className="ops-mdl__truthnext" data-testid="model-next-turn">
            The next turn launches with <code>{c.nextTurn}</code> ({c.picked.label}).
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* The same fact, one line, for the top of the job page: a mismatch is never
   something to scroll down for. */
export function ModelMismatchBanner(props: { job: Job }) {
  const c = props.job.modelCheck;
  if (!c || c.state !== 'mismatch') return null;
  const said = c.reported && c.reported !== c.launched ? c.reported : c.answered;
  return (
    <div className="ops-life ops-life--bad" role="alert" data-testid="model-mismatch">
      <div className="ops-life__head">
        <Badge tone="bad">Wrong model</Badge>
        <span className="ops-life__sub">attempt {c.attempt}</span>
      </div>
      <p className="ops-life__detail">
        Asked for {c.picked.label} (<code>{c.launched}</code>). The session reported <code>{said}</code>.
      </p>
    </div>
  );
}

// ── did the notification actually arrive? (spec §10, §11) ──────────────────
export function AlertsPanel(props: { job: Job }) {
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const jobId = props.job.id;
  useEffect(() => {
    let live = true;
    void api
      .get<{ alerts: Alert[] }>(`/api/jobs/${jobId}/alerts`)
      .then((r) => live && setAlerts(r.alerts ?? []))
      .catch(() => live && setAlerts([]));
    return () => {
      live = false;
    };
  }, [jobId, props.job.lifecycle]);

  if (!alerts || !alerts.length) return null;
  const undelivered = alerts.filter((a) => a.state !== 'sent');

  return (
    <Section
      title="Notifications"
      aside={undelivered.length ? `${undelivered.length} not delivered` : `${alerts.length} delivered`}
    >
      <div className="ops-alr">
        {alerts.map((a) => (
          <div className={`ops-alr__row ops-alr__row--${a.state}`} key={a.id}>
            <span className="ops-alr__mark">
              <BellRing size={14} />
            </span>
            <span className="ops-alr__main">
              <span className="ops-alr__title">{a.title}</span>
              <span className="ops-alr__body">{a.body}</span>
              {a.error ? <span className="ops-alr__err">{a.error}</span> : null}
            </span>
            <span className="ops-alr__end">
              <Badge tone={a.state === 'sent' ? 'ok' : a.state === 'failed' ? 'bad' : 'wait'}>
                {a.state === 'sent' ? 'on your phone' : a.state === 'skipped' ? 'not configured' : a.state}
              </Badge>
              <span className="ops-alr__at">{clock(a.sentAt ?? a.at)}</span>
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ── work the session handed to the daemon (spec §6) ────────────────────────
export function LongJobsPanel(props: { job: Job }) {
  const [rows, setRows] = useState<LongJobView[] | null>(null);
  const jobId = props.job.id;
  useEffect(() => {
    let live = true;
    const read = () =>
      void api
        .get<{ longJobs: LongJobView[] }>(`/api/jobs/${jobId}/long-jobs`)
        .then((r) => live && setRows(r.longJobs ?? []))
        .catch(() => live && setRows([]));
    read();
    const t = setInterval(read, 15_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [jobId]);

  if (!rows || !rows.length) return null;
  return (
    <Section title="Long-running work" aside={`${rows.filter((r) => r.state === 'running').length} running`}>
      <p className="ops-lj__explain">
        Work the session handed to the daemon rather than detaching itself. It runs in its own unit, survives the session
        being replaced, and its result is fed into the next turn.
      </p>
      <div className="ops-lj">
        {rows.map((r) => (
          <div className={`ops-lj__row ops-lj__row--${r.state}`} key={r.id}>
            <span className="ops-lj__mark">
              <TerminalSquare size={14} />
            </span>
            <span className="ops-lj__main">
              <span className="ops-lj__label">{r.label}</span>
              <code className="ops-lj__cmd">{r.command}</code>
            </span>
            <Badge tone={r.state === 'running' ? 'run' : r.exitCode === 0 ? 'ok' : 'bad'}>
              {r.state === 'running' ? 'running' : r.exitCode === 0 ? 'finished' : `exit ${r.exitCode}`}
            </Badge>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* The one line at the top of the page that says what the JOB is doing, as
 * against what its session is doing. "The session said it was done" is a
 * session event and never a completion (spec §11). */
export function LifecycleBanner(props: { job: Job }) {
  const { job } = props;
  if (!job.lifecycle) return null;
  const m = lifeMeta(job.lifecycle);
  const parked = job.parkedUntil ? new Date(job.parkedUntil) : null;
  return (
    <div className={`ops-life ops-life--${m.tone}`} data-testid="job-lifecycle">
      <div className="ops-life__head">
        <Badge tone={m.tone} pulse={m.live}>
          {m.label}
        </Badge>
        {m.sub ? <span className="ops-life__sub">{m.sub}</span> : null}
      </div>
      {job.lifecycleDetail ? <p className="ops-life__detail">{job.lifecycleDetail}</p> : null}
      {parked ? (
        <p className="ops-life__parked">
          It starts again by itself at {clock(parked.toISOString())} on{' '}
          {parked.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}. Nothing is lost.
        </p>
      ) : null}
    </div>
  );
}
